import React from 'react';
import { State, OnActionArgs, OnEnterArgs } from '..';
import { IApi } from 'utils/API';
import { addNotification } from 'utils/NotificationManager';
import { IItem } from 'interfaces';
import { KeysOfType } from 'component_utils/typescriptUtils';
import Translation from 'utils/Language/Translation';
import { isNil } from 'lodash';
import { CompoundBarcode, compoundItemBarcodeParser } from 'utils/Config';
import { MaybePromise, distinctBy } from 'component_utils/utils';
import { itemAutocompleteOptions } from 'component_utils/objectUtils';

type FindItem<S, P, R> = (state: OnActionArgs<S, P, R>) => Promise<[CompoundBarcode, IItem]>;

interface EventProps {
  api: IApi;
}

interface Config<S, P, A, R> {
  nextState: number;
  itemKey: KeysOfType<S, IItem>;
  scannedCodeKey?: KeysOfType<S, String>;

  compoundBarcodeKey?: KeysOfType<S, CompoundBarcode>;
  attributes?: Partial<A>;
  findItem?: FindItem<S, P, R>;
  possibleItems?: (state: OnEnterArgs<S, P, R>) => MaybePromise<IItem[]>,
  skipOnAlreadySet?: boolean
}

const StateItem = function <S, P extends EventProps, A extends { hint: string }, R>({
  nextState,
  itemKey,
  compoundBarcodeKey = '__be_unused' as any,
  attributes = {},
  findItem = async ({ inputs: [input], props }) => {
    const compound = await compoundItemBarcodeParser(
      input,
      (codes) => props.api.item.findMultipleItem(codes)
    )
    return [compound, compound.item]
  },
  possibleItems,
  skipOnAlreadySet = true
}: Config<S, P, A, R>): State<S, P, A, R> {
  return {
    onEnter: async ({ state }) => {
      if (!isNil(state[itemKey]) && skipOnAlreadySet) {
        return {
          type: 'MOVE',
          data: {} as any,
          nextState,
        };
      }
    },
    onAction: async (args) => {
      const d = await findItem(args);
      if (!d || !d[1]) {
        throw addNotification('danger', <Translation name="T.errors.unknownItem" />);
      }
      const [compound, item] = d
      return {
        type: 'MOVE',
        data: {
          [itemKey]: item,
          [compoundBarcodeKey]: compound,
        } as any,
        nextState,
      };
    },
    attributes: async (args) => {
      const { props: { api } } = args
      let autocompleter = api.item.findAutocomplete
      if (possibleItems) {
        const items = distinctBy(await possibleItems(args), it => it.code)
        autocompleter = async s => itemAutocompleteOptions(items, s) as any[]
      }

      return {
        hint: 'T.hints.item',
        autocompleter,
        ...(attributes as any),  
      }
    },

    nodeName: 'T.states.item',
    outputs: [
      {
        name: 'onScan',
        to: nextState,
      },
    ],
  };
};

export default StateItem;
